import { useEffect, useState } from 'react'
import type { RouteComponentProps } from 'react-router'
import { Route } from 'react-router'
import { matchLocaleAwarePath } from 'lib/routing/matchLocaleAwarePath'
import { connect } from 'react-redux'

import loadable from 'components/utils/lazyImport'
import { selectLoggedIn } from 'selectors/accountSelectors'

import { getUpcomingOrders } from 'selectors/orderSelectors'
import { isSupportAssistantEnabled } from 'selectors/featuresSelectors'
import { selectCurrentChat } from 'supportAssistant/selectors/selectCurrentChat'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import useChats from 'supportAssistant/hooks/useChats'

const SupportAssistantChatWidget = loadable(() => import(/* webpackChunkName: 'SupportAssistantChatWidget' */ 'supportAssistant/components/ChatWidget/ChatWidget'), <div />)

const supportAssistantChatWidgetRoute = '/:regionCode'

const allowedCheckoutRoutes = [
  '/:regionCode/checkout/change-dates',
  '/:regionCode/checkout/change-package',
  '/:regionCode/checkout/add-extra-guests',
  '/:regionCode/account/my-escapes/order',
]

const excludedRoutes = [
  '/:regionCode/support',
  '/:regionCode/trip-planner',
  '/:regionCode/order/:orderId/experience-gift-details',
  '/:regionCode/account/communication-preferences/unsubscribe',
  '/:regionCode/booking-authority/share',
]

interface Props extends RouteComponentProps<any> {
  isLoggedIn: boolean
  orders: Array<App.Order>
  isSupportAssistantEnabled: boolean
  currentChat: App.Chat
  pathname: string
}

function SupportAssistantChatWidgetRoutes(props: Props) {
  const { isLoggedIn, orders, isSupportAssistantEnabled, currentChat, pathname } = props
  const [showChatWidget, setShowChatWidget] = useState<boolean>(false)
  const isLESAConditionalBubbleEnabled = !!useOptimizelyExperiment(OptimizelyFeatureFlags.opexLESAConditionalBubble)

  useChats()

  const isAllowedCheckoutRoute = allowedCheckoutRoutes.some(route => matchLocaleAwarePath(pathname, { path: route, exact: false }))
  const isCheckoutRoute = !!matchLocaleAwarePath(pathname, { path: '/:regionCode/checkout', exact: false })
  const isExcludedRoute = excludedRoutes.some(route => matchLocaleAwarePath(pathname, { path: route, exact: false }))
  const isHomePage = !!matchLocaleAwarePath(pathname, { path: '/:regionCode', exact: true })

  const shouldExclude = isExcludedRoute || (isCheckoutRoute && !isAllowedCheckoutRoute)

  useEffect(() => {
    if (showChatWidget || !isSupportAssistantEnabled) {
      return
    }

    const hasActiveChat = currentChat?.status === 'ACTIVE' && currentChat?.id !== 'default'
    const hotelOrBedbankOrders = orders.filter((order) => order.items.length > 0 || order.bedbankItems.length > 0)

    // for non-logged-in users, only show widget if they have an ACTIVE chat that is not the default chat
    if (!isLoggedIn) {
      setShowChatWidget(hasActiveChat)
      return
    }

    if (isLESAConditionalBubbleEnabled) {
      setShowChatWidget(hotelOrBedbankOrders.length > 0 || hasActiveChat)
    } else {
      setShowChatWidget(isHomePage && hotelOrBedbankOrders.length > 0)
    }
  }, [isLoggedIn, orders, showChatWidget, isSupportAssistantEnabled, currentChat?.status, currentChat?.id, isLESAConditionalBubbleEnabled, pathname, isHomePage])

  if (!showChatWidget || !isSupportAssistantEnabled || shouldExclude) {
    return null
  }

  return <Route path={supportAssistantChatWidgetRoute} component={SupportAssistantChatWidget} />
}

function mapStateToProps(state: App.State) {
  return {
    isLoggedIn: selectLoggedIn(state),
    orders: getUpcomingOrders(state),
    isSupportAssistantEnabled: isSupportAssistantEnabled(state),
    currentChat: selectCurrentChat(state),
    pathname: state.router.location.pathname,
  }
}

export default connect(mapStateToProps)(SupportAssistantChatWidgetRoutes)
